import React, { useEffect, useState } from "react";
import ReactSelect from "react-select";
import { useForm, Controller } from "react-hook-form";
import moment from "moment";
import {
  Eye,
  Filter,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Search,
  ListFilter,
  Activity,
  User,
} from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  fetchActivityLogsListData,
  fetchActivityLogById,
  getActivityLogs,
  getActivityLogsTotalRecords,
  getActivityLogsPage,
  getActivityLogsSize,
  getActivityLogsFilter,
  getActivityLogsLoading,
  setFilter,
  resetFilter,
  setPage,
  setSize,
  openDetailModal,
} from "../../features/activityLogs/activityLogsSlice";
import { getSelectedBranch } from "../../features/settings/settingsSlice";
import { fetchUsersListData, getUsers } from "../../features/users/usersSlice";
import { ActivityLogProps, ActivityLogEvent, ActivityLogFilterType } from "../../types/activityLog";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "../ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { filterSelectStyles } from "../../static/styles";
import { t } from "../../locales";
import ActivityLogDetailModal from "./ActivityLogDetailModal";

const listSelectStyles: any = {
  ...filterSelectStyles,
  control: (base: any, state: any) => ({
    ...(typeof filterSelectStyles.control === "function"
      ? (filterSelectStyles.control as any)(base, state)
      : base),
    borderRadius: "0.5rem",
    fontSize: "1rem",
  }),
  singleValue: (base: any) => ({
    ...(typeof filterSelectStyles.singleValue === "function"
      ? (filterSelectStyles.singleValue as any)(base, {})
      : base),
    fontSize: "1rem",
  }),
  placeholder: (base: any) => ({
    ...(typeof filterSelectStyles.placeholder === "function"
      ? (filterSelectStyles.placeholder as any)(base, {})
      : base),
    fontSize: "1rem",
  }),
  input: (base: any) => ({ ...base, fontSize: "1rem" }),
};

const eventBadgeClass = (event: string) => {
  switch (event) {
    case "created":
      return "bg-emerald-600 hover:bg-emerald-700 text-white";
    case "updated":
      return "bg-blue-600 hover:bg-blue-700 text-white";
    case "deleted":
      return "bg-rose-600 hover:bg-rose-700 text-white";
    default:
      return "bg-gray-500 hover:bg-gray-600 text-white";
  }
};

const eventLabel = (event: string) => {
  const labels = t.activityLogs as any;
  const map: Record<string, string> = {
    created: labels.eventCreated ?? "Created",
    updated: labels.eventUpdated ?? "Updated",
    deleted: labels.eventDeleted ?? "Deleted",
  };
  return map[event] ?? event;
};

const moduleOptions = [
  "Account",
  "AccountType",
  "BranchSetting",
  "Items",
  "ItemSize",
  "Parties",
  "PartiesItems",
  "Payments",
  "ProcessOrder",
  "ProcessReceive",
  "Processes",
  "Sizes",
  "User",
];

const eventOptions = [
  { value: "created", label: (t.activityLogs as any).eventCreated ?? "Created" },
  { value: "updated", label: (t.activityLogs as any).eventUpdated ?? "Updated" },
  { value: "deleted", label: (t.activityLogs as any).eventDeleted ?? "Deleted" },
];

const moduleLabels = (t.activityLogs as any).modules ?? {};
const moduleSelectOptions = moduleOptions.map((m) => ({
  value: m,
  label: moduleLabels[m] ?? m,
}));

const ActivityLogsList: React.FC = () => {
  const dispatch = useAppDispatch();
  const labels = t.activityLogs as any;

  const records = useAppSelector(getActivityLogs);
  const totalRecords = useAppSelector(getActivityLogsTotalRecords);
  const page = useAppSelector(getActivityLogsPage);
  const size = useAppSelector(getActivityLogsSize);
  const filter = useAppSelector(getActivityLogsFilter);
  const loading = useAppSelector(getActivityLogsLoading);
  const selectedBranch = useAppSelector(getSelectedBranch);
  const users = useAppSelector(getUsers);

  const userSelectOptions = users.map((u) => ({
    value: u.id,
    label: u.email ? `${u.name} (${u.email})` : u.name,
  }));

  const [showFilters, setShowFilters] = useState(false);

  const { register, control, handleSubmit, reset } = useForm<ActivityLogFilterType>({
    defaultValues: {
      search: filter.search ?? "",
      model_type: filter.model_type ?? null,
      user_id: filter.user_id ?? null,
      event: filter.event ?? null,
      fromDate: filter.fromDate ?? "",
      toDate: filter.toDate ?? "",
    },
  });

  useEffect(() => {
    if (selectedBranch) {
      dispatch(fetchActivityLogsListData({ page, size, filter }));
    }
  }, [dispatch, page, size, filter, selectedBranch]);

  useEffect(() => {
    dispatch(fetchUsersListData({ page: 1, size: 1000 }));
  }, [dispatch]);

  const totalPages = Math.max(1, Math.ceil(totalRecords / size));
  const start = totalRecords === 0 ? 0 : (page - 1) * size + 1;
  const end = Math.min(page * size, totalRecords);

  const getVisiblePages = (): (number | string)[] => {
    const pages: (number | string)[] = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (page > 3) pages.push("...");
      for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) pages.push(i);
      if (page < totalPages - 2) pages.push("...");
      pages.push(totalPages);
    }
    return pages;
  };

  const onApplyFilters = (data: ActivityLogFilterType) => {
    dispatch(setFilter({
      search: (data.search as any)?.trim() || null,
      model_type: data.model_type || null,
      user_id: data.user_id ?? null,
      event: data.event || null,
      fromDate: data.fromDate || null,
      toDate: data.toDate || null,
    }));
  };

  const handleClearFilters = () => {
    reset({ search: "", model_type: null, user_id: null, event: null, fromDate: "", toDate: "" });
    dispatch(resetFilter());
  };

  const onView = (log: ActivityLogProps) => {
    dispatch(openDetailModal(log));
    dispatch(fetchActivityLogById(log.id));
  };

  return (
    <div className="space-y-4">
      {/* Action row */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="flex gap-3 ml-auto">
          <Button
            variant="outline"
            className="h-10 px-4 rounded-lg border-gray-300 text-[#113F67] hover:bg-gray-50 hover:text-[#113F67] text-sm"
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter className="h-4 w-4 mr-2" />
            {labels.filterToggle ?? "Filters"}
          </Button>
        </div>
      </div>

      {/* Filters card */}
      {showFilters && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">{labels.filterTitle ?? "Filters"}</CardTitle>
          </CardHeader>
          <CardContent className="pt-0 pb-4">
            <form onSubmit={handleSubmit(onApplyFilters)}>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 items-end">
                {/* Search */}
                <div>
                  <label className="text-sm font-semibold text-[#113F67] mb-1 block">
                    {labels.searchLabel ?? "Search"}
                  </label>
                  <div className="relative">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-[#34699A]" />
                    <Input
                      className="pl-12 h-[48px] text-base border-2 border-gray-200 rounded-lg focus:border-[#113F67] focus:shadow-focus-brand"
                      placeholder={labels.searchPlaceholder ?? "Search description, module, IP..."}
                      {...register("search")}
                    />
                  </div>
                </div>

                {/* Module */}
                <div>
                  <label className="text-sm font-semibold text-[#113F67] mb-1 block">
                    {labels.filterModule ?? "Module"}
                  </label>
                  <div className="relative">
                    <ListFilter className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#34699A] z-10 pointer-events-none" />
                    <Controller
                      name="model_type"
                      control={control}
                      render={({ field }) => (
                        <ReactSelect
                          styles={listSelectStyles}
                          options={moduleSelectOptions}
                          placeholder={labels.allModules ?? "All modules"}
                          value={moduleSelectOptions.find((o) => o.value === field.value) ?? null}
                          onChange={(opt: any) => field.onChange(opt?.value ?? null)}
                          isClearable
                        />
                      )}
                    />
                  </div>
                </div>

                {/* Event */}
                <div>
                  <label className="text-sm font-semibold text-[#113F67] mb-1 block">
                    {labels.filterEvent ?? "Event"}
                  </label>
                  <div className="relative">
                    <Activity className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#34699A] z-10 pointer-events-none" />
                    <Controller
                      name="event"
                      control={control}
                      render={({ field }) => (
                        <ReactSelect
                          styles={listSelectStyles}
                          options={eventOptions}
                          placeholder={labels.allEvents ?? "All events"}
                          value={eventOptions.find((o) => o.value === field.value) ?? null}
                          onChange={(opt: any) => field.onChange((opt?.value as ActivityLogEvent) ?? null)}
                          isClearable
                        />
                      )}
                    />
                  </div>
                </div>

                {/* User */}
                <div>
                  <label className="text-sm font-semibold text-[#113F67] mb-1 block">
                    {labels.filterUser ?? "User"}
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#34699A] z-10 pointer-events-none" />
                    <Controller
                      name="user_id"
                      control={control}
                      render={({ field }) => (
                        <ReactSelect
                          styles={listSelectStyles}
                          options={userSelectOptions}
                          placeholder={labels.allUsers ?? "All users"}
                          value={userSelectOptions.find((o) => o.value === field.value) ?? null}
                          onChange={(opt: any) => field.onChange(opt?.value ?? null)}
                          isClearable
                        />
                      )}
                    />
                  </div>
                </div>

                {/* From Date */}
                <div>
                  <label className="text-sm font-semibold text-[#113F67] mb-1 block">
                    {labels.filterFromDate ?? "From Date"}
                  </label>
                  <Input
                    type="date"
                    className="h-[48px] text-base border-2 border-gray-200 rounded-lg focus:border-[#113F67] focus:shadow-focus-brand"
                    {...register("fromDate")}
                  />
                </div>

                {/* To Date */}
                <div>
                  <label className="text-sm font-semibold text-[#113F67] mb-1 block">
                    {labels.filterToDate ?? "To Date"}
                  </label>
                  <Input
                    type="date"
                    className="h-[48px] text-base border-2 border-gray-200 rounded-lg focus:border-[#113F67] focus:shadow-focus-brand"
                    {...register("toDate")}
                  />
                </div>
              </div>

              <div className="flex gap-3 mt-4 border-t pt-4 justify-end">
                <Button
                  type="button"
                  variant="outline"
                  className="h-10 px-4 rounded-lg border-gray-300 text-[#113F67] hover:bg-gray-50 hover:text-[#113F67] text-sm"
                  onClick={handleClearFilters}
                >
                  {labels.clearFilters ?? "Clear Filters"}
                </Button>
                <Button
                  type="submit"
                  className="h-10 px-4 rounded-lg bg-[#113F67] hover:bg-[#0d2f4f] border border-[#113F67] text-sm"
                >
                  {labels.applyFilters ?? "Apply Filters"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Mobile cards */}
      <div className="md:hidden space-y-3">
        {loading && records.length === 0 ? (
          <Card>
            <CardContent className="py-8">
              <div className="flex items-center justify-center gap-2 text-gray-500">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#113F67]" />
                {labels.loading ?? "Loading..."}
              </div>
            </CardContent>
          </Card>
        ) : !loading && records.length === 0 ? (
          <Card>
            <CardContent className="py-8 text-center text-gray-500">
              {labels.noRecords ?? "No activity logs found"}
            </CardContent>
          </Card>
        ) : (
          <div className={`space-y-3${loading ? " opacity-50 pointer-events-none" : ""}`}>
            {records.map((r, idx) => (
              <Card key={r.id}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-2">
                      <span className="text-sm font-medium text-gray-400">
                        #{(page - 1) * size + idx + 1}
                      </span>
                      <div>
                        <div className="font-semibold text-gray-800 text-sm">{r.model_label}</div>
                        <div className="text-xs text-gray-500">
                          {labels.colRecordId ?? "Record ID"}: {r.model_id}
                        </div>
                      </div>
                    </div>
                    <Badge className={eventBadgeClass(r.event)}>{eventLabel(r.event)}</Badge>
                  </div>

                  <div className="mt-3 pt-3 border-t space-y-1.5 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">{labels.colTimestamp ?? "Timestamp"}</span>
                      <span className="text-gray-700">
                        {moment(r.created_at).format("DD MMM YYYY, hh:mm A")}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">{labels.colUser ?? "User"}</span>
                      <span className="text-gray-700">
                        {r.user?.name ?? (labels.systemUser ?? "System")}
                      </span>
                    </div>
                  </div>

                  <div className="flex justify-end mt-2 pt-2 border-t gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 rounded-lg hover:bg-[#113F67]/10"
                      onClick={() => onView(r)}
                    >
                      <Eye className="h-4 w-4 text-[#113F67]" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Desktop table */}
      <Card className="hidden md:block">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">{labels.colSr ?? "#"}</TableHead>
                  <TableHead>{labels.colTimestamp ?? "Timestamp"}</TableHead>
                  <TableHead>{labels.colUser ?? "User"}</TableHead>
                  <TableHead>{labels.colModule ?? "Module"}</TableHead>
                  <TableHead>{labels.colEvent ?? "Event"}</TableHead>
                  <TableHead>{labels.colRecordId ?? "Record ID"}</TableHead>
                  <TableHead className="text-right">{labels.colActions ?? "Actions"}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8">
                      <div className="flex items-center justify-center gap-2 text-gray-500">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#113F67]" />
                        {labels.loading ?? "Loading..."}
                      </div>
                    </TableCell>
                  </TableRow>
                ) : records.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                      {labels.noRecords ?? "No activity logs found"}
                    </TableCell>
                  </TableRow>
                ) : (
                  records.map((r, idx) => (
                    <TableRow key={r.id} className="align-top">
                      <TableCell className="font-medium text-gray-500">
                        {(page - 1) * size + idx + 1}
                      </TableCell>
                      <TableCell className="text-sm whitespace-nowrap">
                        <div className="text-gray-700">
                          {moment(r.created_at).format("DD-MM-YYYY")}
                        </div>
                        <div className="text-xs text-gray-400">
                          {moment(r.created_at).format("hh:mm A")}
                        </div>
                      </TableCell>
                      <TableCell>
                        {r.user?.name ?? (
                          <span className="text-gray-400">{labels.systemUser ?? "System"}</span>
                        )}
                      </TableCell>
                      <TableCell className="font-medium text-[#113F67]">{r.model_label}</TableCell>
                      <TableCell>
                        <Badge className={eventBadgeClass(r.event)}>{eventLabel(r.event)}</Badge>
                      </TableCell>
                      <TableCell className="text-gray-600">{r.model_id}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 rounded-lg hover:bg-[#113F67]/10"
                            onClick={() => onView(r)}
                          >
                            <Eye className="h-4 w-4 text-[#113F67]" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Pagination */}
      {!loading && records.length > 0 && (
        <div className="flex flex-wrap items-center justify-end md:justify-between gap-4">
          <div className="hidden md:flex items-center gap-2 text-sm text-gray-600">
            <span>
              {start}-{end} of {totalRecords}
            </span>
            <span>{labels.pageSizeLabel ?? "Per page:"}</span>
            <Select
              value={String(size)}
              onValueChange={(v) => dispatch(setSize(Number(v)))}
            >
              <SelectTrigger className="w-20 h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              className="h-8 w-8 p-0"
              onClick={() => dispatch(setPage(1))}
              disabled={page === 1}
            >
              <ChevronsLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              className="h-8 w-8 p-0"
              onClick={() => dispatch(setPage(Math.max(1, page - 1)))}
              disabled={page === 1}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            {getVisiblePages().map((p, i) =>
              typeof p === "string" ? (
                <span key={`e-${i}`} className="px-2 text-gray-400">
                  ...
                </span>
              ) : (
                <Button
                  key={p}
                  variant={page === p ? "default" : "outline"}
                  className={`w-8 h-8 p-0 ${page === p ? "bg-[#113F67] hover:bg-[#0d2f4d]" : ""}`}
                  onClick={() => dispatch(setPage(p))}
                >
                  {p}
                </Button>
              )
            )}
            <Button
              variant="outline"
              className="h-8 w-8 p-0"
              onClick={() => dispatch(setPage(Math.min(totalPages, page + 1)))}
              disabled={page === totalPages}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              className="h-8 w-8 p-0"
              onClick={() => dispatch(setPage(totalPages))}
              disabled={page === totalPages}
            >
              <ChevronsRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      <ActivityLogDetailModal />
    </div>
  );
};

export default ActivityLogsList;
